export function createInitialGroupReservationForm() {
  return {
    group_type: "general",
    visit_start_date: "",
    visit_end_date: "",
    preferred_time: "",
    group_name: "",
    headcount: "",
    adult_count: "",
    child_count: "",
    leader_count: "",
    preferred_package_id: "",
    option_product_ids: [],
    option_product_quantities: {},
    request_memo: "",
    applicant_name: "",
    applicant_phone: "",
    applicant_email: "",
    privacy_agreed: false,
  };
}

const trimText = (value) => String(value ?? "").trim();

const parseOptionalNumber = (value) => {
  const trimmed = trimText(value);
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseRequiredNumber = (value) => {
  const parsed = parseOptionalNumber(value);
  return parsed === null ? 0 : parsed;
};

const parsePositiveInteger = (value) => {
  const parsed = parseOptionalNumber(value);
  return parsed !== null && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const isValidApplicantPhone = (value) => /^01[016789]-?\d{3,4}-?\d{4}$/.test(trimText(value));

export function formatPhoneNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "").slice(0, 11);
  if (digits.length < 4) return digits;
  if (digits.length < 8) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}

export function getTodayDateString() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const GROUP_RESERVATION_ERROR_ORDER = [
  "visit_start_date",
  "visit_end_date",
  "group_name",
  "headcount",
  "applicant_name",
  "applicant_phone",
  "privacy_agreed",
];

const parseDate = (value) => {
  const trimmed = trimText(value);
  if (!trimmed) return null;
  const [year, month, day] = trimmed.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

const minutesFromTime = (value) => {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return hour * 60 + minute;
};

const formatMinutes = (value) => {
  const hour = Math.floor(value / 60);
  const minute = value % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const parseOperatingHourRange = (value) => {
  const match = String(value || "").match(/(\d{1,2}:\d{2})\s*[-~]\s*(\d{1,2}:\d{2})/);
  if (!match) return null;
  const start = minutesFromTime(match[1]);
  const end = minutesFromTime(match[2]);
  if (start === null || end === null || end <= start) return null;
  return { start, end };
};

export function buildPreferredTimeOptions(form, config = {}) {
  const visitDate = parseDate(form.visit_start_date);
  const operatingHours = visitDate && isWeekend(visitDate)
    ? config.weekend_holiday_operating_hours
    : config.weekday_operating_hours;
  const range = parseOperatingHourRange(operatingHours);
  if (!range) return ["상담 후 결정"];

  const options = [];
  for (let minute = range.start; minute < range.end; minute += 60) {
    options.push(formatMinutes(minute));
  }
  return options.length ? options : ["상담 후 결정"];
}

const selectedOptionIds = (form) =>
  Array.isArray(form.option_product_ids)
    ? form.option_product_ids.map((item) => Number(item)).filter((item) => Number.isInteger(item) && item > 0)
    : [];

const optionQuantityFor = (form, optionId) => {
  const quantities = form.option_product_quantities || {};
  const parsedQuantity = parsePositiveInteger(quantities[optionId] ?? quantities[String(optionId)]);
  if (parsedQuantity !== null) return parsedQuantity;
  const headcount = parsePositiveInteger(form.headcount);
  return headcount || 1;
};

const selectedOptionProducts = (form) =>
  selectedOptionIds(form).map((optionId) => ({
    option_product_id: optionId,
    quantity: optionQuantityFor(form, optionId),
  }));

export function calculateGroupReservationOptionEstimate(form, optionProducts = []) {
  const startDate = parseDate(form.visit_start_date);
  const endDate = parseDate(form.visit_end_date || form.visit_start_date);
  const selections = selectedOptionProducts(form);
  const quantityById = new Map(selections.map((item) => [item.option_product_id, item.quantity]));
  if (!startDate || !endDate || endDate < startDate || selections.length === 0) {
    return { items: [], totalAmount: 0 };
  }

  const items = optionProducts
    .filter((optionProduct) => quantityById.has(Number(optionProduct.id)))
    .map((optionProduct) => {
      const optionId = Number(optionProduct.id);
      const quantity = quantityById.get(optionId) || 1;
      let currentDate = new Date(startDate.getTime());
      let unitPrice = 0;
      while (currentDate <= endDate) {
        unitPrice += Number(isWeekend(currentDate) ? optionProduct.weekend_price : optionProduct.weekday_price) || 0;
        currentDate.setDate(currentDate.getDate() + 1);
      }
      return {
        id: optionId,
        name: optionProduct.name,
        weekdayPrice: Number(optionProduct.weekday_price) || 0,
        weekendPrice: Number(optionProduct.weekend_price) || 0,
        unitPrice,
        quantity,
        totalAmount: unitPrice * quantity,
      };
    });

  return {
    items,
    totalAmount: items.reduce((sum, item) => sum + item.totalAmount, 0),
  };
}

export function validateGroupReservationForm(form) {
  const errors = {};
  const headcount = parseRequiredNumber(form.headcount);
  const groupType = trimText(form.group_type) || "general";

  if (!trimText(form.visit_start_date)) {
    errors.visit_start_date = "희망 방문 시작일을 선택해주세요.";
  }
  if (form.visit_start_date && form.visit_end_date && form.visit_end_date < form.visit_start_date) {
    errors.visit_end_date = "희망 방문 종료일은 시작일보다 빠를 수 없습니다.";
  }
  if (!trimText(form.group_name)) {
    errors.group_name = "단체명 또는 기관명을 입력해주세요.";
  }
  if (headcount < 2) {
    errors.headcount = "총 인원은 2명 이상이어야 합니다.";
  }
  if (groupType === "academy") {
    const detailCount =
      parseRequiredNumber(form.adult_count) +
      parseRequiredNumber(form.child_count) +
      parseRequiredNumber(form.leader_count);
    if (detailCount > headcount) {
      errors.headcount = "세부 인원 합계는 총 인원을 초과할 수 없습니다.";
    }
  }
  if (!trimText(form.applicant_name)) {
    errors.applicant_name = "신청자명을 입력해주세요.";
  }
  if (!trimText(form.applicant_phone)) {
    errors.applicant_phone = "신청자 연락처를 입력해주세요.";
  } else if (!isValidApplicantPhone(form.applicant_phone)) {
    errors.applicant_phone = "010-0000-0000 형식의 연락처를 입력해주세요.";
  }
  if (!form.privacy_agreed) {
    errors.privacy_agreed = "개인정보 수집 및 이용에 동의해주세요.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function buildGroupReservationPayload(form, options = {}) {
  const groupType = trimText(form.group_type) || "general";
  const payload = {
    group_type: groupType,
    group_name: trimText(form.group_name),
    applicant_name: trimText(form.applicant_name),
    applicant_phone: trimText(form.applicant_phone),
    applicant_email: trimText(form.applicant_email) || null,
    visit_start_date: trimText(form.visit_start_date),
    visit_end_date: trimText(form.visit_end_date) || trimText(form.visit_start_date),
    preferred_time: trimText(form.preferred_time) || null,
    headcount: parseRequiredNumber(form.headcount),
    adult_count: groupType === "academy" ? parseOptionalNumber(form.adult_count) : null,
    child_count: groupType === "academy" ? parseOptionalNumber(form.child_count) : null,
    leader_count: groupType === "academy" ? parseOptionalNumber(form.leader_count) : null,
    preferred_package_id: null,
    option_product_ids: selectedOptionIds(form),
    option_products: selectedOptionProducts(form),
    request_memo: trimText(form.request_memo) || null,
    privacy_agreed: Boolean(form.privacy_agreed),
  };

  if (options.agencyToken) {
    payload.agency_token = trimText(options.agencyToken);
  }

  return payload;
}
